import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "~/components/ui/chart";
import type { ChartConfig } from "~/components/ui/chart";
import { useV2HistoricalData } from "~/hooks/useV2Data";
import { AlertCircle } from "lucide-react";

interface V2HistoricalChartsProps {
  days?: number;
}

const collateralChartConfig = {
  totalCollateral: {
    label: "Total Collateral",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

const debtChartConfig = {
  totalDebt: {
    label: "Total Debt",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const interestRateChartConfig = {
  interestRate: {
    label: "Interest Rate",
    color: "hsl(var(--chart-3))",
  },
} satisfies ChartConfig;

const ltvChartConfig = {
  maxOriginationLtv: {
    label: "Max Origination LTV",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

export function V2HistoricalCharts({ days = 150 }: V2HistoricalChartsProps) {
  const { data, isLoading, error } = useV2HistoricalData(days);

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGOHM = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (timestamp: number) => {
    try {
      if (isNaN(timestamp) || timestamp <= 0) {
        console.warn("Invalid timestamp value:", timestamp);
        return "";
      }
      // Convert from microseconds to milliseconds by dividing by 1000
      const date = new Date(timestamp / 1000);
      if (date.toString() === "Invalid Date") {
        console.warn("Invalid date from timestamp:", timestamp);
        return "";
      }
      return date.toLocaleDateString();
    } catch (error) {
      console.error("Error converting timestamp:", timestamp, error);
      return "";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-96 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Failed to load historical data</p>
        </div>
      </div>
    );
  }

  const chartData = data.map(point => ({
    ...point,
    date: formatDate(point.timestamp),
  })).filter(point => point.date) // Filter out entries with invalid dates
    .reverse(); // Reverse to show chronologically (oldest to newest) since query returns newest first

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Historical Charts</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Collateral Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Total Collateral Over Time</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ChartContainer config={collateralChartConfig}>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value.split("-").slice(1).join("/")}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickFormatter={(value) => `${formatGOHM(value)} gOHM`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line
                      type="monotone"
                      dataKey="totalCollateral"
                      stroke="var(--color-totalCollateral)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Debt Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Total Debt Over Time</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ChartContainer config={debtChartConfig}>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value.split("-").slice(1).join("/")}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickFormatter={formatUSD} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line
                      type="monotone"
                      dataKey="totalDebt"
                      stroke="var(--color-totalDebt)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* LTV Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>LTV Over Time (USDS per gOHM)</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ChartContainer config={ltvChartConfig}>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value.split("-").slice(1).join("/")}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis domain={['auto', 'auto']} tickFormatter={formatUSD} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line
                      type="monotone"
                      dataKey="maxOriginationLtv"
                      stroke="var(--color-maxOriginationLtv)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Interest Rate Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Interest Rate Over Time</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ChartContainer config={interestRateChartConfig}>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => value.split("-").slice(1).join("/")}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickFormatter={formatPercentage} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line
                      type="monotone"
                      dataKey="interestRate"
                      stroke="var(--color-interestRate)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 